/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import Plot from "react-plotly.js";
import { saveAs } from "file-saver";

const MonteCarloOptionSimulator = () => {
  const [S0, setS0] = useState(100);
  const [K, setK] = useState(100);
  const [T, setT] = useState(1);
  const [r, setR] = useState(0.05);
  const [sigma, setSigma] = useState(0.2);
  const [simulations, setSimulations] = useState(1000);
  const [steps, setSteps] = useState(100);
  const [optionType, setOptionType] = useState("call");
  const [optionPrice, setOptionPrice] = useState(null);
  const [confidenceInterval, setConfidenceInterval] = useState([]);
  const [paths, setPaths] = useState([]);
  const [finalPayoffs, setFinalPayoffs] = useState([]);

  const generateSimulations = () => {
    const dt = T / steps;
    const paths = [];
    const payoffs = [];

    for (let i = 0; i < simulations; i++) {
      let prices = [S0];
      for (let j = 0; j < steps; j++) {
        const z = Math.random() * 2 - 1; // approx standard normal
        const dS = prices[prices.length - 1] * Math.exp((r - 0.5 * sigma ** 2) * dt + sigma * Math.sqrt(dt) * z);
        prices.push(dS);
      }
      paths.push(prices);
      const ST = prices[prices.length - 1];
      const payoff = optionType === "call" ? Math.max(ST - K, 0) : Math.max(K - ST, 0);
      payoffs.push(payoff);
    }

    const discountedPayoffs = payoffs.map(p => p * Math.exp(-r * T));
    const mean = discountedPayoffs.reduce((a, b) => a + b, 0) / discountedPayoffs.length;
    const stdDev = Math.sqrt(discountedPayoffs.map(x => (x - mean) ** 2).reduce((a, b) => a + b, 0) / discountedPayoffs.length);
    const ci = [mean - 1.96 * stdDev / Math.sqrt(simulations), mean + 1.96 * stdDev / Math.sqrt(simulations)];

    setPaths(paths);
    setOptionPrice(mean);
    setConfidenceInterval(ci);
    setFinalPayoffs(discountedPayoffs);
  };

  const handleExportCSV = () => {
    const headers = ["Step", ...Array.from({ length: steps + 1 }, (_, i) => `Path${i + 1}`)];
    const csvRows = [];

    for (let i = 0; i <= steps; i++) {
      const row = [i, ...paths.map(p => p[i].toFixed(2))];
      csvRows.push(row.join(","));
    }

    csvRows.unshift(headers.join(","));
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "montecarlo_option_paths.csv");
  };

  useEffect(() => {
    generateSimulations();
  }, [S0, K, T, r, sigma, simulations, steps, optionType]);

  return (
<div style={{height:"auto" }}>
    <div className="col-md-6 col-lg-12 wow fadeInUp" style={{ marginBottom: "20px",height:"auto" }} data-wow-delay="0.6s">
      <div className="service-item">
        <h2>Monte Carlo Option Pricing ({optionType.toUpperCase()})         <img
          src="https://img.icons8.com/color/48/microsoft-excel-2019--v1.png"
          alt="Export CSV"
          title="Export to CSV"
          style={{ width: "30px", height: "30px", cursor: "pointer", alignSelf: "center" }}
          onClick={handleExportCSV}
        /></h2>
        <div style={{align:"center"}} >
          <label>Option Type:
          <select value={optionType} onChange={e => setOptionType(e.target.value)}>
            <option value="call">Call</option>
            <option value="put">Put</option>
          </select>
        </label>
        </div>
      {/*<div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>*/}
        <label>Stock Price: {S0}<input type="range" min={1} max={100} step={1} value={S0} onChange={e => setS0(+e.target.value)} /></label>
        <label>Strike Price: {K}<input type="range"  min={1} max={130} step={1} value={K} onChange={e => setK(+e.target.value)} /></label>
        <label>Maturity (yrs): {T}<input type="range"  min={0} max={20} step={0.25} value={T} onChange={e => setT(+e.target.value)} /></label>
        <label>Volatility: {sigma} <input type="range" min={0} max={0.4} step={0.01}  step="0.01" value={sigma} onChange={e => setSigma(+e.target.value)} /></label>
        {/*<label>Rate: <input type="range" step="0.01" value={r} onChange={e => setR(+e.target.value)} /></label>*/}
        <label>Simulations: {simulations} <input type="range"  min={100} max={1000} step={10} value={simulations} onChange={e => setSimulations(+e.target.value)} /></label>
        <label>Steps: {steps} <input type="range"  min={50} max={100} step={10} value={steps} onChange={e => setSteps(+e.target.value)} /></label>

      <div style={{ marginTop: 20 }}>

      </div>

      <Plot
        data={[
          ...paths.slice(0, 50).map((path, i) => ({
            x: Array.from({ length: steps + 1 }, (_, j) => j),
            y: path,
            type: "scatter",
            mode: "lines",
            name: `Sim ${i + 1}`,
            line: { width: 1 },
            hoverinfo: "none",
          })),
          {
            x: finalPayoffs,
            type: "histogram",
            marker: { color: "green", opacity: 0.5 },
            name: "Payoffs",
            xaxis: "x2",
            yaxis: "y2",
          }
        ]}
        layout={{
          title: "Simulated Stock Paths & Payoff Distribution",
          grid: { rows: 2, columns: 1, pattern: "independent" },
          height: 600,
          showlegend: false,
          xaxis: { title: "Steps" },
          yaxis: { title: "Stock Price" },
          xaxis2: { title: "Option Payoff" },
          yaxis2: { title: "Frequency" },
        }}
        // style={{ width: "100%", height: "700px", maxWidth: "1000px" }}
      />
          {/* VaR Info Box */}
  <div
    style={{
      position: "absolute",
      top: "950px",
      right: "800px",
      background: "white",
      border: "1px solid #ccc",
      padding: "10px",
      borderRadius: "8px",
      fontSize: "14px",
      lineHeight: "1.4",
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      zIndex: 10,
    }}
  >
    <strong>📊 Summary</strong><br />
        <strong>Option Price:</strong> ₹{optionPrice?.toFixed(2)} <br />
        <strong>95% CI:</strong> ₹{confidenceInterval[0]?.toFixed(2)} – ₹{confidenceInterval[1]?.toFixed(2)}
  </div>
      </div>
    </div>
    </div>
  );
};

export default MonteCarloOptionSimulator;
